import type { MaintenanceWindowData, MetricDataQuery } from '@amzn/aws-gsaas-antenna-lambda-client';
import { GroundstationAntennaLambda } from '@amzn/aws-gsaas-antenna-lambda-client';
import type { TransformerCapability } from '@amzn/aws-gsaas-mgmt-cell-capability-device-config-client';
import { GroundStationMgmtCellCapabilityDeviceConfigLambda } from '@amzn/aws-gsaas-mgmt-cell-capability-device-config-client';
import type { AlarmHistoryItem, MetricDataQuery as CwMetricDataQuery } from '@aws-sdk/client-cloudwatch';
import {
  CloudWatchClient,
  DescribeAlarmHistoryCommand,
  DescribeAlarmsCommand,
  GetMetricDataCommand,
  ListMetricsCommand,
} from '@aws-sdk/client-cloudwatch';
import type { AwsCredentialIdentity } from '@aws-sdk/types';
import type { AntennaSystem, AwsAccount } from '@amzn/aws-gsaas-accounts-helper';
import {
  accountsHelper,
  getAntennaName,
  getAntennaSystemAccounts,
  getAntennaToAccountMap,
} from '../utils/accountsUtils';
import { getRoleArn } from '../utils/gsRoleUtils';
import type { DateRange } from '../hooks/useSearchParamDateRange';
import type { AlarmSeries, AlarmStatePoint } from '../types/transformerAlarmTypes';
import type { LinkDatapoint, LinkState, NetworkLinkStatus } from '../types/networkLinkTypes';
import { buildAwsTemporaryCredentialProvider } from './buildAwsTemporaryCredentialProvider';
import { ClientManager } from './ClientManager';
import { InFlightPromiseThrottler } from '@amzn/aws-gsaas-common-typescript/PromiseThrottler';

// Map colo codes to airport codes to regional airport codes
export const COLO_CODES_TO_AIRPORT_MAP = new Map<string, string>([
  ['dbo', 'syd'],
  ['puq', 'gru'],
  ['scc', 'pdx'],
  ['hnl', 'pdx'],
  ['lab', 'iad'],
  ['xba', 'fra'],
]);

export function toMaintenanceWindow(window: MaintenanceWindowData, antenna: string) {
  const descriptionData: MaintenanceWindowAdditionalInfo =
    window.additionalInfo === undefined
      ? { stepFunctionExecutionId: 'undefined' }
      : (JSON.parse(window.additionalInfo) as MaintenanceWindowAdditionalInfo);
  return {
    data: descriptionData,
    antennaName: antenna,
    ...window,
  };
}

export interface MaintenanceWindow extends MaintenanceWindowData {
  data: MaintenanceWindowAdditionalInfo;
  antennaName?: string;
}
export interface MaintenanceWindowAdditionalInfo {
  stepFunctionExecutionId: string;
}
/**
 * Transform into more easily accessible antenna system account (non-null)
 */
export interface AntennaSystemAwsAccount extends AwsAccount {
  antenna_system: AntennaSystem;
}

/**
 * How long discovered DX/VPN link ids stay cached per antenna (1 hour). The
 * set of ids a cell publishes only changes on infrastructure changes (a new
 * DX connection or VPN), so re-running ListMetrics discovery on every refetch
 * is wasted calls; the hourly re-discovery still picks up rare changes.
 */
const LINK_DISCOVERY_TTL_MS = 60 * 60 * 1000;

interface DiscoveredLinkIds {
  readonly dxIds: string[];
  readonly vpnIds: string[];
  readonly fetchedAt: number;
}

export class MgmtCellClientManager extends ClientManager<GroundstationAntennaLambda> {
  private antennasSystemAccounts: AntennaSystemAwsAccount[] | undefined;
  private antennaToAccountMap: Map<string, AntennaSystemAwsAccount> | undefined;
  private antennaToGroundstationMap = new Map<string, Map<string, AntennaSystemAwsAccount[]>>();
  private cancelMaintenanceThrottler = new InFlightPromiseThrottler(10);
  private readonly capabilityDeviceClientMap = new Map<string, GroundStationMgmtCellCapabilityDeviceConfigLambda>();
  private readonly cloudWatchClientMap = new Map<string, CloudWatchClient>();
  private readonly linkIdsCacheMap = new Map<string, DiscoveredLinkIds>();

  /**
   * Load management cell accounts with defined antenna systems
   */
  public getAntennaSystems(): AntennaSystemAwsAccount[] {
    if (this.antennasSystemAccounts !== undefined) {
      return this.antennasSystemAccounts;
    }
    const accounts = [
      ...accountsHelper.getAccounts('mgmt', this.stage),
      ...accountsHelper.getAccounts('mgmt-das73', this.stage),
    ];
    const antennaSystemAccounts = accounts
      .filter(account => account.antenna_systems !== undefined && account.antenna_systems.length > 0)
      .map(account => ({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        antenna_system: account.antenna_systems![0],
        ...account,
      })) as AntennaSystemAwsAccount[];
    this.antennasSystemAccounts = antennaSystemAccounts;
    return this.antennasSystemAccounts;
  }

  public getAntennaToAccountMap(): Map<string, AntennaSystemAwsAccount> {
    if (this.antennaToAccountMap !== undefined) {
      return this.antennaToAccountMap;
    }
    this.antennaToAccountMap = new Map(this.getAntennaSystems().map(account => [account.antenna_system.id, account]));
    return this.antennaToAccountMap;
  }

  public async loadMaintenanceWindows(props: {
    antenna: string;
    startTime: Date;
    endTime: Date;
  }): Promise<MaintenanceWindow[]> {
    let nextToken: string | undefined;
    const maintenanceList: MaintenanceWindow[] = [];
    do {
      const client = this.getAntennaControlClientForCell({
        antenna: props.antenna,
      });
      const response = await client.listMaintenance({
        startTime: props.startTime,
        endTime: props.endTime,
        maxResults: 100,
      });
      nextToken = response.nextToken;
      const windows: MaintenanceWindow[] = (response.windows ?? []).map(window =>
        toMaintenanceWindow(window, props.antenna),
      );
      maintenanceList.push(...windows);
    } while (nextToken != null);
    return maintenanceList;
  }

  public async loadMaintenanceWindowsForSites(props: { siteIds: string[]; startTime: Date; endTime: Date }) {
    const maintenanceList: MaintenanceWindow[] = [];

    const antennaNames = getAntennaSystemAccounts(this.stage)
      .filter(antennaSystemAccount => props.siteIds.includes(antennaSystemAccount.antenna_system.ground_station_id))
      .map(antennaSystemAccount => getAntennaName(antennaSystemAccount, this.stage));

    await Promise.all(
      antennaNames.map(async antenna => {
        const antennaMaintenaceList = await this.loadMaintenanceWindows({
          antenna,
          startTime: props.startTime,
          endTime: props.endTime,
        });
        maintenanceList.push(...antennaMaintenaceList);
      }),
    );

    return maintenanceList;
  }

  public async deleteMaintenanceWindow(props: { antenna: string; reservationId: string }) {
    const client = this.getAntennaControlClientForCell({
      antenna: props.antenna,
    });
    const response = await this.cancelMaintenanceThrottler.run(() =>
      client.cancelMaintenance({
        maintenanceReservationId: props.reservationId,
      }),
    );
    return response.maintenanceReservationId;
  }

  public async getAntennaMetrics(props: {
    antenna: string;
    metricDataQuery: MetricDataQuery[];
    startTime: Date;
    endTime: Date;
  }) {
    const client = this.getAntennaControlClientForCell({
      antenna: props.antenna,
    });
    const response = await client.getMetrics({
      StartTime: props.startTime,
      EndTime: props.endTime,
      MetricDataQueries: props.metricDataQuery,
    });
    return response.MetricDataResults;
  }

  public async deleteAllMaintenanceWindows(props: { reservations: MaintenanceWindow[] }) {
    return Promise.all(
      props.reservations.map(id =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.deleteMaintenanceWindow({ antenna: id.antennaName!, reservationId: id.maintenanceReservationId! }),
      ),
    );
  }

  public async describeMaintenance(props: { antenna: string; reservationId: string }) {
    const client = this.getAntennaControlClientForCell({
      antenna: props.antenna,
    });
    return await client.describeMaintenance({
      maintenanceReservationId: props.reservationId,
    });
  }

  public async scheduleMaintenanceWindowAsync(props: {
    antenna: string;
    dateRange: DateRange;
    justification: string;
    user: string;
    contactsToCancel: string[];
    requestReason: string;
    deploymentWindow: boolean;
  }) {
    const client = this.getAntennaControlClientForCell({
      antenna: props.antenna,
    });
    const response = await client.scheduleMaintenanceAsync({
      startTime: props.dateRange.startDate,
      endTime: props.dateRange.endDate,
      justification: props.justification,
      user: props.user,
      deploymentWindow: props.deploymentWindow,
      schedulingReason: props.requestReason,
      contactsToCancel: props.contactsToCancel,
    });
    return response.maintenanceReservationId;
  }

  private getAntennaControlClientForCell(opts: { antenna: string }): GroundstationAntennaLambda {
    const localizedClient = this.clientMap.get(opts.antenna);
    if (localizedClient !== undefined) return localizedClient;

    // Create new client, cache it, return it, and set a timeout to refresh it later
    const newClient = this.buildAntennaControlClientForCell({
      antenna: opts.antenna,
      gsocRegion: this.gsocRegion,
      stage: this.stage,
      username: this.username,
      credentials: this.credentials,
    });
    this.clientMap.set(opts.antenna, newClient);

    return newClient;
  }

  /**
   * List every capability assignment in an antenna's mgmt cell, fleet-wide.
   *
   * One `ListTransformerCapabilitiesV2` call (`GET /transformer-capabilities`)
   * returns the assignments for all hosts at once - each row carries its
   * `transformerName`, so callers group per host without a call per host. The
   * per-transformer v1 route is deprecated and no longer used; the assumed
   * cross-account role (`Ops@ReadSiteAntennaMetrics`) allow-lists the v2 route.
   */
  public async loadTransformerCapabilities(opts: { antenna: string }): Promise<TransformerCapability[]> {
    const client = this.getCapabilityDeviceClientForCell({
      antenna: opts.antenna,
    });

    const response = await client.listTransformerCapabilitiesV2({});
    return response.capabilities ?? [];
  }

  private getCapabilityDeviceClientForCell(opts: {
    antenna: string;
  }): GroundStationMgmtCellCapabilityDeviceConfigLambda {
    const localizedClient = this.capabilityDeviceClientMap.get(opts.antenna);
    if (localizedClient !== undefined) return localizedClient;

    // Create new client, cache it, return it, and set a timeout to refresh it later
    const newClient = this.buildCapbilityDeviceClientForCell({
      antenna: opts.antenna,
      gsocRegion: this.gsocRegion,
      stage: this.stage,
      username: this.username,
      credentials: this.credentials,
    });
    this.capabilityDeviceClientMap.set(opts.antenna, newClient);

    return newClient;
  }

  /**
   * Fetch the last hour of one per-transformer alarm's STATE timeline (M1 part C
   * time-series), from CloudWatch `DescribeAlarmHistory`. Returns the ordered
   * state changes over the window (oldest-first), seeded with the state in effect
   * at the window start so the plotted line covers the full hour. This is the
   * actual OK / ALARM / INSUFFICIENT_DATA the alarm evaluated to, computed by
   * CloudWatch (threshold + evaluation-periods already applied) - not the raw
   * underlying metric.
   *
   * Fetched lazily (on expand), not eagerly per page load, since it is an extra
   * call per alarm. Returns null when the alarm has no history / does not exist.
   */
  public async getTransformerAlarmSeries(opts: {
    antenna: string;
    serverName: string;
    suffix: string;
  }): Promise<AlarmSeries | null> {
    const transformerNum = opts.serverName.replace(/\D/g, '');
    if (transformerNum === '') return null;
    const alarmName = `Transformer${transformerNum}${opts.suffix}`;
    const client = this.getCloudWatchClientForCell({ antenna: opts.antenna });

    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - 60 * 60 * 1000);
    // Paginate: the default page size (50) can be exceeded by a flapping
    // alarm within the hour, and dropping the oldest items would mis-seed
    // the window start.
    const items: AlarmHistoryItem[] = [];
    let historyToken: string | undefined;
    do {
      const response = await client.send(
        new DescribeAlarmHistoryCommand({
          AlarmName: alarmName,
          AlarmTypes: ['MetricAlarm'],
          HistoryItemType: 'StateUpdate',
          StartDate: startTime,
          EndDate: endTime,
          ScanBy: 'TimestampDescending',
          NextToken: historyToken,
        }),
      );
      items.push(...(response.AlarmHistoryItems ?? []));
      historyToken = response.NextToken;
    } while (historyToken !== undefined);

    // Each StateUpdate item's HistoryData is JSON with { oldState, newState }, each
    // carrying a `stateValue`. Build oldest-first transitions from the newState.
    const parseState = (item: AlarmHistoryItem, which: 'oldState' | 'newState'): string | undefined => {
      if (item.HistoryData === undefined) return undefined;
      try {
        const data = JSON.parse(item.HistoryData) as {
          oldState?: { stateValue?: string };
          newState?: { stateValue?: string };
        };
        return data[which]?.stateValue;
      } catch {
        return undefined;
      }
    };

    // Items are newest-first (TimestampDescending); reverse to oldest-first.
    const ascending = [...items].reverse();
    const points: AlarmStatePoint[] = [];
    for (const item of ascending) {
      const state = parseState(item, 'newState');
      if (item.Timestamp !== undefined && state !== undefined) points.push({ x: item.Timestamp, state });
    }

    // Seed the window start so the line covers the full hour: use the oldState of
    // the earliest transition in the window (that is what the alarm was before it),
    // or, if there were no transitions, the alarm's current state.
    const earliest = ascending[0];
    let seedState: string | undefined = earliest !== undefined ? parseState(earliest, 'oldState') : undefined;
    if (seedState === undefined) {
      const describe = await client.send(
        new DescribeAlarmsCommand({ AlarmNames: [alarmName], AlarmTypes: ['MetricAlarm'] }),
      );
      seedState = (describe.MetricAlarms ?? []).find(a => a.AlarmName === alarmName)?.StateValue;
    }
    const first = points[0];
    if (seedState !== undefined && (first === undefined || first.x.getTime() > startTime.getTime())) {
      points.unshift({ x: startTime, state: seedState });
    }

    // Extend the last known state to "now" so the step line reaches the window end.
    const last = points[points.length - 1];
    if (last === undefined) return null;
    if (last.x.getTime() < endTime.getTime()) points.push({ x: endTime, state: last.state });
    return { points };
  }

  /**
   * Fetch per-antenna DX and VPN link status from the mgmt-cell account's
   * CloudWatch (M1 part D). Discovers connection ids with `ListMetrics`
   * (DX = `AWS/DX ConnectionState` per `ConnectionId`; VPN = `AWS/VPN
   * TunnelState` per `VpnId`), then reads the last HOUR of datapoints per id with
   * `GetMetricData`. VPN uses the `Maximum` statistic so a `VpnId` reads up (1)
   * when at least one of its two tunnels is up. Each `LinkState` carries both the
   * latest reading (`value`: 1 = up, 0 = down, undefined = no datapoint) for the
   * status indicator AND the 1-hour `series` (oldest-first) for the time-series
   * plot the operator opens by clicking the id.
   *
   * Read-only, CloudWatch only (no EC2 / Direct Connect clients): ListMetrics
   * discovery keeps everything within the CloudWatch read surface, avoiding
   * extra clients. The assumed role (`Ops@ReadSiteAntennaMetrics`) grants the
   * CloudWatch reads (`ListMetrics`/`GetMetricData` were added to the role for
   * M1).
   *
   * Discovered ids are cached per antenna for an hour: which link ids a cell
   * publishes only changes on infrastructure changes, so refetches skip the
   * ListMetrics calls and go straight to GetMetricData (which must stay fresh
   * - it carries the up/down values).
   */
  public async getNetworkLinkStatus(opts: { antenna: string }): Promise<NetworkLinkStatus> {
    const client = this.getCloudWatchClientForCell({ antenna: opts.antenna });

    // 1. Discover the connection/vpn ids present in this account via ListMetrics,
    //    or reuse the cached discovery when it is fresh enough. Empty results are
    //    cached too - a cell that publishes no DX (or no VPN) stays that way, and
    //    "None published" refetches should not re-discover every time.
    const discover = async (namespace: string, metricName: string, dimensionName: string): Promise<string[]> => {
      const ids = new Set<string>();
      let nextToken: string | undefined;
      do {
        const page = await client.send(
          new ListMetricsCommand({ Namespace: namespace, MetricName: metricName, NextToken: nextToken }),
        );
        for (const metric of page.Metrics ?? []) {
          const dim = (metric.Dimensions ?? []).find(d => d.Name === dimensionName);
          if (dim?.Value !== undefined) ids.add(dim.Value);
        }
        nextToken = page.NextToken;
      } while (nextToken !== undefined);
      return [...ids].sort();
    };

    const cached = this.linkIdsCacheMap.get(opts.antenna);
    let dxIds: string[];
    let vpnIds: string[];
    if (cached !== undefined && Date.now() - cached.fetchedAt < LINK_DISCOVERY_TTL_MS) {
      ({ dxIds, vpnIds } = cached);
    } else {
      // Only cache on success: a failed discovery propagates as today and the
      // next call retries rather than serving a bad (or empty-by-error) set.
      [dxIds, vpnIds] = await Promise.all([
        discover('AWS/DX', 'ConnectionState', 'ConnectionId'),
        discover('AWS/VPN', 'TunnelState', 'VpnId'),
      ]);
      this.linkIdsCacheMap.set(opts.antenna, { dxIds, vpnIds, fetchedAt: Date.now() });
    }

    // 2. Read the latest datapoint per id. DX uses Minimum (single link); VPN
    //    uses Maximum so a VpnId is up when at least one of its tunnels is up.
    const queries: CwMetricDataQuery[] = [
      ...dxIds.map((id, i) => ({
        Id: `dx${i}`,
        MetricStat: {
          Metric: {
            Namespace: 'AWS/DX',
            MetricName: 'ConnectionState',
            Dimensions: [{ Name: 'ConnectionId', Value: id }],
          },
          Period: 300,
          Stat: 'Minimum',
        },
      })),
      ...vpnIds.map((id, i) => ({
        Id: `vpn${i}`,
        MetricStat: {
          Metric: {
            Namespace: 'AWS/VPN',
            MetricName: 'TunnelState',
            Dimensions: [{ Name: 'VpnId', Value: id }],
          },
          Period: 300,
          Stat: 'Maximum',
        },
      })),
    ];

    const directConnect: LinkState[] = dxIds.map(id => ({ id, value: undefined, series: [] }));
    const vpn: LinkState[] = vpnIds.map(id => ({ id, value: undefined, series: [] }));

    if (queries.length > 0) {
      const endTime = new Date();
      // 1-hour window: the tab shows the latest state, and clicking an id opens a
      // time-series plot of this window. At Period 300 that is up to ~12 points.
      const startTime = new Date(endTime.getTime() - 60 * 60 * 1000);
      const response = await client.send(
        new GetMetricDataCommand({
          MetricDataQueries: queries,
          StartTime: startTime,
          EndTime: endTime,
          // Newest datapoint first, so Values[0] is the latest reading.
          ScanBy: 'TimestampDescending',
        }),
      );
      for (const r of response.MetricDataResults ?? []) {
        if (r.Id === undefined) continue;
        // Values are most-recent-first (TimestampDescending); Timestamps align by
        // index. Values[0] is the latest reading; the series is oldest-first for
        // the chart (reverse the descending arrays).
        const values = r.Values ?? [];
        const timestamps = r.Timestamps ?? [];
        const latest = values[0];
        const series: LinkDatapoint[] = [];
        for (let i = values.length - 1; i >= 0; i--) {
          const ts = timestamps[i];
          const y = values[i];
          if (ts !== undefined && y !== undefined) series.push({ x: ts, y });
        }
        const target = r.Id.startsWith('dx') ? directConnect : vpn;
        const idx = Number(r.Id.slice(r.Id.startsWith('dx') ? 2 : 3));
        const existing = target[idx];
        if (existing !== undefined) target[idx] = { id: existing.id, value: latest, series };
      }
    }

    return { directConnect, vpn };
  }

  private getCloudWatchClientForCell(opts: { antenna: string }): CloudWatchClient {
    const localizedClient = this.cloudWatchClientMap.get(opts.antenna);
    if (localizedClient !== undefined) return localizedClient;

    const newClient = this.buildCloudWatchClientForCell({
      antenna: opts.antenna,
      gsocRegion: this.gsocRegion,
      stage: this.stage,
      username: this.username,
      credentials: this.credentials,
    });
    this.cloudWatchClientMap.set(opts.antenna, newClient);

    return newClient;
  }

  private buildCloudWatchClientForCell(opts: {
    credentials: AwsCredentialIdentity;
    username: string;
    stage: string;
    antenna: string;
    gsocRegion: string;
  }): CloudWatchClient {
    const account = this.getAntennaToAccountMap().get(opts.antenna);
    if (account === undefined) {
      throw new Error(`Cannot locate management cell account for ${opts.antenna} and ${opts.stage}`);
    }

    // Same cross-account role as the capability-device client. CloudWatch is an
    // AWS SDK client (not the mgmt-cell API Gateway), so there is NO endpoint
    // override and no execute-api allow-list; it must sign in the mgmt cell's
    // own region (e.g. cmh51 = us-east-2), like the other per-cell clients.
    const airportCode = account.cell_partition.substring(0, 3).toLowerCase();
    const airportCodeLocal = COLO_CODES_TO_AIRPORT_MAP.get(airportCode) ?? airportCode;
    const roleName =
      this.permission !== undefined ? `Ops@${this.permission}@${opts.stage}@${airportCodeLocal}` : this.clientRole;
    const roleArn = getRoleArn(account.account_id, roleName);

    return new CloudWatchClient({
      credentials: buildAwsTemporaryCredentialProvider({
        roleArn,
        username: opts.username,
        masterCredentials: opts.credentials,
        region: opts.gsocRegion,
      }),
      region: account.region,
    });
  }

  private buildAntennaControlClientForCell(opts: {
    credentials: AwsCredentialIdentity;
    username: string;
    stage: string;
    antenna: string;
    gsocRegion: string;
  }): GroundstationAntennaLambda {
    const account = getAntennaToAccountMap(this.stage)[opts.antenna];
    if (account === undefined) {
      throw new Error(`Cannot locate management cell account for ${opts.antenna} and ${opts.stage}`);
    }
    const service = account.services.find(service => service.service_key === 'managementcell');
    if (service === undefined) {
      throw new Error(`Failed to locate service managementcell for ${account.account_id}`);
    }
    console.debug('Found endpoint: ', service.endpoint);

    // Quick way to get airport code for antennas
    const airportCode = account.cell_partition.substring(0, 3).toLowerCase();
    const airportCodeLocal = COLO_CODES_TO_AIRPORT_MAP.get(airportCode) ?? airportCode;
    const roleName =
      this.permission !== undefined ? `Ops@${this.permission}@${opts.stage}@${airportCodeLocal}` : this.clientRole;
    console.debug('Found service role name: ', roleName);
    const roleArn = getRoleArn(account.account_id, roleName);

    return new GroundstationAntennaLambda({
      credentials: buildAwsTemporaryCredentialProvider({
        roleArn,
        username: opts.username,
        masterCredentials: opts.credentials,
        region: opts.gsocRegion,
      }),
      region: account.region,
      endpoint: `https://${service.endpoint}`,
    });
  }

  private buildCapbilityDeviceClientForCell(opts: {
    credentials: AwsCredentialIdentity;
    username: string;
    stage: string;
    antenna: string;
    gsocRegion: string;
  }): GroundStationMgmtCellCapabilityDeviceConfigLambda {
    const account = this.getAntennaToAccountMap().get(opts.antenna);
    if (account === undefined) {
      throw new Error(`Cannot locate management cell account for ${opts.antenna} and ${opts.stage}`);
    }
    const service = account.services.find(service => service.service_key === 'capabilitydeviceconfig');
    if (service === undefined) {
      throw new Error(`Failed to locate service managementcell for ${account.account_id}`);
    }
    console.debug('Found endpoint: ', service.endpoint);

    // Quick way to get airport code for antennas
    const airportCode = account.cell_partition.substring(0, 3).toLowerCase();
    const airportCodeLocal = COLO_CODES_TO_AIRPORT_MAP.get(airportCode) ?? airportCode;
    const roleName =
      this.permission !== undefined ? `Ops@${this.permission}@${opts.stage}@${airportCodeLocal}` : this.clientRole;
    console.debug('Found service role name: ', roleName);
    const roleArn = getRoleArn(account.account_id, roleName);

    return new GroundStationMgmtCellCapabilityDeviceConfigLambda({
      credentials: buildAwsTemporaryCredentialProvider({
        roleArn,
        username: opts.username,
        masterCredentials: opts.credentials,
        region: opts.gsocRegion,
      }),
      region: account.region,
      endpoint: `https://${service.endpoint}`,
    });
  }
}
